// ✅ src/pages/safety/SafetyView.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { saveSafetyDoc, getSafetyDocDetail } from "../../api/safety";
import { softDeleteDoc } from "../../api/papers";

// 모바일 전용 FormViewManager (웹사이트 영향 방지)
import MobileFormViewManager from "../../components/common/mobile/MobileFormViewManager";
import MobileShareButton from "../../components/common/mobile/MobileShareButton";

// --- 뷰 & 에디터 ---
import ReportView from "../../components/safety/view/ReportView";
import ChecklistView from "../../components/safety/view/ChecklistView";
import RiskcheckView from "../../components/safety/view/RiskcheckView";
import IncidentView from "../../components/safety/view/IncidentView";
// 모바일 전용 SCARView (웹사이트 영향 방지)
import MobileSCARView from "../../components/safety/view/MobileSCARView";

import PlanEditor from "../../components/safety/editor/ReportEditor";
import ChecklistEditor from "../../components/safety/editor/ChecklistEditor";
import RiskcheckEditor from "../../components/safety/editor/RiskcheckEditor";
import IncidentEditor from "../../components/safety/editor/IncidentEditor";
// 모바일 전용 SCAREditor (웹사이트 영향 방지)
import MobileSCAREditor from "../../components/safety/editor/MobileSCAREditor";

// ------------------------------------------------------------------
export default function SafetyViewer() {
  const nav = useNavigate();
  const { doc_type, dns } = useParams();
  const projectId = localStorage.getItem("projectId");
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return { id: Number(parsed.id), name: parsed.name || "알 수 없음" };
    } catch {
      return {};
    }
  }, []);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const isOwner = String(doc?.created_by) === String(currentUser?.name);

  useEffect(() => {
    if (!doc || !doc.meta) return;

    const refs = doc.meta.reference_users;
    const me = Number(currentUser?.id);

    // 1) 전체 열람 가능 ALL
    if (refs === "ALL") return;

    // 2) 배열인데 내가 포함되지 않은 경우
    if (Array.isArray(refs) && !refs.includes(me)) {
      alert("접근 권한이 없습니다.");
      nav(-1); // 뒤로가기
    }
  }, [doc, currentUser, nav]);

  // === 문서 로드 ===
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getSafetyDocDetail(dns);
        if (!alive) return;
        setDoc(data);
      } catch (e) {
        setError(e?.message || "문서를 불러오지 못했습니다.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dns]);

  // === 저장 ===
  const handleSave = async () => {
    if (!draft) return;
    try {
      setSaving(true);
      const payload = {
        dns,
        project: projectId,
        title: draft?.title || doc?.title || "(제목 없음)",
        doc_type: doc?.doc_type || doc_type,
        status: doc?.status || "none",
        meta: draft?.meta || draft || {},
      };
      await saveSafetyDoc(payload);
      alert("저장 완료");
      setIsEditing(false);
      const refreshed = await getSafetyDocDetail(dns);
      setDoc(refreshed);
      setDraft(null);
    } catch (e) {
      console.error(e);
      alert("저장 실패");
    } finally {
      setSaving(false);
    }
  };

  // === 삭제 ===
  const handleDelete = async () => {
    if (!window.confirm("이 문서를 삭제하시겠습니까?")) return;
    try {
      await softDeleteDoc(dns, true);
      alert("문서가 삭제되었습니다.");
      nav(`/project/${projectId}/safety/board`, { replace: true });
    } catch (e) {
      alert("삭제 실패");
    }
  };

  // === 매핑 ===
  // 모바일 환경에서는 SCARView 대신 MobileSCARView 사용 (웹사이트 영향 방지)
  const VIEW_BY_TYPE = {
    report: ReportView,
    check: ChecklistView,
    scar: MobileSCARView, // 모바일 전용
    incident: IncidentView,
    riskcheck: RiskcheckView,
  };
  // 모바일 환경에서는 SCAREditor 대신 MobileSCAREditor 사용 (웹사이트 영향 방지)
  const EDITOR_BY_TYPE = {
    report: PlanEditor,
    check: ChecklistEditor,
    scar: MobileSCAREditor, // 모바일 전용
    incident: IncidentEditor,
    riskcheck: RiskcheckEditor,
  };

  const ViewComp = VIEW_BY_TYPE[doc_type];
  const EditComp = EDITOR_BY_TYPE[doc_type];

  if (loading)
    return (
      <div
        className="mobile-page-root mb-16"
        style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
      >
        <div
          className="mobile-page-root-inner space-y-3"
          style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
        >
          <div className="bg-white rounded-lg p-4 border animate-pulse">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
              <div className="h-8 w-8 bg-gray-100 rounded-full" />
            </div>
            <div className="mt-3 flex justify-between">
              <div className="h-3 bg-gray-100 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-1/4" />
            </div>
          </div>
        </div>
      </div>
    );
  if (error)
    return (
      <div
        className="mobile-page-root mb-16"
        style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
      >
        <div
          className="mobile-page-root-inner space-y-3"
          style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
        >
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {error}
          </div>
        </div>
      </div>
    );
  if (!doc)
    return (
      <div
        className="mobile-page-root mb-16"
        style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
      >
        <div
          className="mobile-page-root-inner space-y-3"
          style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
        >
          <div className="bg-white rounded-lg p-8 text-center border">
            <p className="text-sm text-gray-500">문서가 없습니다.</p>
          </div>
        </div>
      </div>
    );

  // === formType 명시용 ===
  const FORM_LABEL = {
    report: "안전관리 계획서",
    plan: "안전 점검 일정",
    result: "안전 점검 결과",
    scar: "안전 시정 조치 요구서",
    incident: "안전 사고 보고서",
    riskcheck: "위험성 평가서",
  };

  const docName = `${FORM_LABEL[doc_type] || "안전문서"
    } (${doc_type.toUpperCase()})`;

  // === 렌더링 ===
  return (
    <div
      className="mobile-page-root mb-16"
      style={{ width: '100%', maxWidth: '100vw', minWidth: 0, display: 'flex', justifyContent: 'center', overflowX: 'hidden', boxSizing: 'border-box' }}
    >
      <div
        className="mobile-page-root-inner space-y-3"
        style={{ width: '100%', maxWidth: '480px', margin: '0 auto', padding: '0 1rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}
      >
        <MobileFormViewManager
          params={{
            project: projectId,
            dns,
            module: "safety",
            doc_type,
          }}
          doc_name={docName}
        >
          <div className="w-full min-w-0 space-y-4 overflow-hidden">
            {isEditing && EditComp ? (
              <div className="min-w-0 overflow-hidden">
                <EditComp
                  initial={{
                    ...doc.meta, // 메타 필드
                    title: doc.title, //Changed: 제목 전달
                    doc_date: doc.doc_date, //Changed: 작성일도 전달하면 더 정확
                  }}
                  onChange={setDraft}
                  readOnly={false}
                  currentUser={currentUser}
                />
              </div>
            ) : (
              <div className="min-w-0 overflow-hidden">
                {ViewComp && <ViewComp doc={doc} currentUser={currentUser} />}
              </div>
            )}

            {/* ✅ 문서 하단 조작 버튼 - 모바일 UX 개선 */}
            {isOwner && (
              <div className="no-print flex flex-wrap justify-end gap-2 mt-6 pt-4 border-t border-gray-200 min-w-0">
                {!isEditing ? (
                  <>
                    <button
                      onClick={() => {
                        if (doc_type === "scar" && doc?.meta?.result_reply) {
                          alert("결과가 등록된 문서는 수정할 수 없습니다.");
                          return; // 수정모드 진입 차단
                        }
                        setIsEditing(true);
                      }}
                      className="btn-edit flex-shrink-0 whitespace-nowrap text-xs px-3 py-1.5"
                    >
                      수정
                    </button>
                    <button
                      onClick={() => {
                        if (doc_type === "scar" && doc?.meta?.result_reply) {
                          alert("결과가 등록된 문서는 삭제할 수 없습니다.");
                          return;
                        }
                        handleDelete();
                      }}
                      className="btn-delete flex-shrink-0 whitespace-nowrap text-xs px-3 py-1.5"
                    >
                      삭제
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="btn-save flex-shrink-0 whitespace-nowrap text-xs px-3 py-1.5 disabled:opacity-60"
                    >
                      {saving ? "저장 중..." : "저장"}
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="btn-cancel flex-shrink-0 whitespace-nowrap text-xs px-3 py-1.5"
                    >
                      취소
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </MobileFormViewManager>
      </div>
    </div>
  );
}
